import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel, ProductAutocompleteItemModel } from "@insite/client-framework/Types/ApiModels";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import getColor from "@insite/mobius/utilities/getColor";
import property from "lodash/property"
import React from "react";
import { css } from "styled-components";

interface Props {
    products: ProductAutocompleteItemModel[];
    focusedItem?: AutocompleteItemModel;
    goToUrl: (url: string) => void;
    extendedStyles?: AutocompleteProductsStyles;
    totalCount: string;
    url: string;
}

export interface AutocompleteProductsStyles {
    headerText?: TypographyPresentationProps;
    container?: GridContainerProps;
    focusedContainer?: GridContainerProps;
    imageGridItem?: GridItemProps;
    image?: LazyImageProps;
    infoGridItem?: GridItemProps;
    titleLink?: LinkPresentationProps;
    erpNumberText?: TypographyPresentationProps;
    hedaerContainer?: GridContainerProps;
    hedaerGridItem?: GridItemProps;
    headerProductCount?: TypographyPresentationProps;
    headerSeeAllResults?: LinkPresentationProps;
    brandNameText?: TypographyPresentationProps;
}

export const autocompleteProductsStyles: AutocompleteProductsStyles = {
    headerText: {
        weight: "bold",
        css: css`
            text-align: left;
            margin: 10px 0 5px 0;
            padding-bottom: 6px;
        `,
    },
    container: {
        gap: 0,
        css: css`
            flex-grow: 0;
            margin: 5px 0;
            cursor: default;
        `,
    },
    focusedContainer: {
        gap: 0,
        css: css`
            flex-grow: 0;
            margin: 5px 0;
            cursor: default;
            background-color: lightgray;
        `,
    },
    imageGridItem: {
        width: 3,
        css: css`
            max-width: 65px;
            max-height: 50px;
            flex-basis: 65px;
        `,
    },
    image: {
        width: "50px",
        css: css`
            img {
                height: 100%;
                cursor: pointer;
            }
        `,
    },
    infoGridItem: {
        width: 9,
        css: css`
            flex-direction: column;
            max-width: calc(100% - 65px);
            flex-basis: calc(100% - 65px);
        `,
    },
    titleLink: {
        typographyProps: {
            ellipsis: false,
            css: css`
                width: 100%;
                text-align: left;
                margin: 0;
                font-size: 14px;
                font-weight: normal;
                line-height: normal;
                cursor: pointer;
                font-style: normal;
                padding-left: 0;
                color: ${getColor("text.main")} !important;
                &:hover {
                    text-decoration: underline;
                }
            `,
        },
        css: css`
            width: auto;
            cursor: default;
            > span {
                width: auto;
                max-width: max-content;
            }
        `,
    },
    erpNumberText: {
        ellipsis: false,
        css: css`
            width: 100%;
            text-align: left;
            margin: 5px 0 0 0;
            font-size: 11px;
            font-weight: normal;
            line-height: normal;
            color: ${getColor("text.accent")};
            font-style: normal;
        `,
    },

    hedaerGridItem: {
        css: css`
            display: flex;
            flex-direction: row;
            flex-basis: 100%;
            max-width: 100%;
            align-items: center;
            margin: 0 0 19px 0 !important;
            > ${TypographyStyle} {
                flex: none;
            }
        `,
    },
    headerSeeAllResults: {
        css: css`
            display: flex;
            justify-content: flex-end;
            flex: 1;
            text-decoration: none;
            color: ${getColor("primary")};
            text-align: right;
            font-size: 12px;
            font-weight: 600;
            line-height: normal;
            padding-top: 1.5px;
        `,
    },
    headerProductCount: {
        css: css`
            color: ${getColor("text.accent")};
            display: inline-block;
            font-family: "Nunito Sans";
            font-size: 14px;
            padding: 0 5px;
        `,
    },
    brandNameText: {
        css: css`
            color: #${getColor("text.main")};
            font-size: 11px;
            font-weight: bold;
            line-height: normal;
            font-style: normal;
            padding-bottom: 5px;
            &:hover {
                text-decoration: underline;
                cursor: pointer;
            }
        `,
    },
};

const styles = autocompleteProductsStyles;

class AutocompleteProducts extends React.Component<Props> {
    private readonly styles: AutocompleteProductsStyles;

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
    }
    handleSeeAllResults = () => {
        window.location.href = `/search?query=${this.props.url}`;
    };
    render() {
        const { products } = this.props;
        const styles = this.styles;

        return (
            <>
                <GridContainer {...styles.hedaerContainer}>
                    <GridItem {...styles.hedaerGridItem}>
                        <Typography {...styles.headerText}>{translate("Products")}</Typography>
                        <Typography {...styles.headerProductCount}>
                            &nbsp;{translate(`(showing ${products.length} of ${this.props.totalCount})`)}
                        </Typography>
                        <StyledWrapper {...styles.headerSeeAllResults}>
                            <Link onClick={this.handleSeeAllResults}>{translate("See All Results")}</Link>
                        </StyledWrapper>
                    </GridItem>
                </GridContainer>

                {products.map(product => (
                    <GridContainer
                        {...(this.props.focusedItem === product ? styles.focusedContainer : styles.container)}
                        key={product.id!}
                    >
                        <GridItem {...styles.imageGridItem}>
                            <LazyImage {...styles.image} src={product.image} onClick={() => {
                                this.props.goToUrl(product.url);
                            }} />
                        </GridItem>
                        <GridItem {...styles.infoGridItem}>
                            <Typography {...styles.brandNameText} onClick={() => {
                                    this.props.goToUrl(product.properties.branD_PATH);
                                }}
                            >
                                {product.brandName}
                            </Typography>
                            <Link {...styles.titleLink} onClick={() => {
                                this.props.goToUrl(product.url);
                            }}>
                                {product.displayTitle}
                            </Link>
                            {(product.properties["modelNo"] || product.manufacturerItemNumber) &&(
                                <Typography {...styles.erpNumberText}>
                                    {product.properties["modelNo"] && product.properties["modelNo"] !== "" 
                                        ? product.properties["modelNo"] 
                                        : product.manufacturerItemNumber} 
                                    &nbsp;|&nbsp;{product.displayErpNumber}
                                </Typography>
                            )}
                        </GridItem>
                    </GridContainer>
                ))}
            </>
        );
    }
}

export default AutocompleteProducts;
